import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../../shared/components/Header';
import Footer from '../../shared/components/Footer';
import { fetchLobbyHistory } from '../../shared/api/stats';
import { fetchCurrentUser, deleteDraft } from '../../shared/api/draftData';
import { suspendDraft } from '../../shared/api/draft';
import { clearFrozenSave } from '../../shared/utils/saveStorage';
import {
  LobbyHistoryItem,
  LobbyHistoryTeam,
  LobbyPlayerAttempt,
  LobbyDraftedPokemon,
} from '../../types';
import { getCachedAvatarUrl } from '../../shared/utils/user';
import './HistoryPage.scss';

function formatPokemonName(name: string): string {
  const lower = name.toLowerCase();
  if (lower.startsWith("farfetch'd")) {
    return "farfetch'd";
  }
  return lower.replace(/'/g, '');
}

function getTimeAgo(dateString?: string): string {
  if (!dateString) return '-';
  const now = new Date();
  const created = new Date(dateString);
  const diffInSeconds = Math.floor((now.getTime() - created.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ${diffInMinutes % 60}m ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `${diffInDays}d ago`;
  return created.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getPlacementBadge(placement: number | null, racePlacement: number | null): { label: string; className: string } | null {
  const p = racePlacement ?? placement;
  if (p === null || p === undefined) return null;
  if (p === 1) return { label: '1st Place', className: 'placement-1st' };
  if (p === 2) return { label: '2nd Place', className: 'placement-2nd' };
  if (p === 3) return { label: '3rd Place', className: 'placement-3rd' };
  return { label: `${p}th Place`, className: 'placement-other' };
}

function formatDraftTypeLabel(type: string, ranked: boolean): string {
  if (type === '1v1') return '1v1 Draft';
  if (type === 'solo') return 'Solo Run';
  if (ranked) return 'Ranked Auction';
  return 'Casual Auction';
}

function formatDraftTypeClass(type: string, ranked: boolean): string {
  if (type === '1v1') return 'type-1v1';
  if (type === 'solo') return 'type-solo';
  if (ranked) return 'type-ranked';
  return 'type-casual';
}

const HistoryPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userParam = searchParams.get('user_id') || searchParams.get('userId');
  const userNameParam = searchParams.get('user') || searchParams.get('username');
  const hasUserScope = Boolean(userParam || userNameParam);

  const initialScope: 'all' | 'my' | 'user' = hasUserScope
    ? 'user'
    : searchParams.get('scope') === 'my' || searchParams.get('tab') === 'my'
    ? 'my'
    : 'all';
  const [scope, setScope] = useState<'all' | 'my' | 'user'>(initialScope);
  const [currentUser, setCurrentUser] = useState<{
    user_id: string | null;
    username: string | null;
    is_guest: boolean;
  } | null>(null);

  const [lobbies, setLobbies] = useState<LobbyHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sync scope when searchParams change (e.g. browser back/forward or external link)
  useEffect(() => {
    const uParam = searchParams.get('user_id') || searchParams.get('userId');
    const uNameParam = searchParams.get('user') || searchParams.get('username');
    if (uParam || uNameParam) {
      setScope('user');
    } else if (searchParams.get('scope') === 'my' || searchParams.get('tab') === 'my') {
      setScope('my');
    } else {
      setScope('all');
    }
  }, [searchParams]);

  // Filters & Pagination
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [formatFilter, setFormatFilter] = useState<'all' | 'ranked' | 'casual' | '1v1' | 'solo'>('all');
  const [suspendedFilter, setSuspendedFilter] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 15;

  // Expanded card tracking
  const [expandedDraftIds, setExpandedDraftIds] = useState<Set<string>>(new Set());

  // Fetch current user on mount
  useEffect(() => {
    fetchCurrentUser()
      .then((u) => {
        setCurrentUser(u);
      })
      .catch((err) => {
        console.warn('[HistoryPage] Could not fetch current user:', err);
      });
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to page 1 on new search
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Cancel Active Run state
  const [cancelingLobby, setCancelingLobby] = useState<LobbyHistoryItem | null>(null);
  const [isCanceling, setIsCanceling] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => {
      setToast((prev) => (prev === msg ? null : prev));
    }, 4000);
  }, []);

  const handlePromptCancelRun = useCallback((lobby: LobbyHistoryItem, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setCancelingLobby(lobby);
  }, []);

  const handleConfirmCancelRun = useCallback(async () => {
    if (!cancelingLobby) return;
    const target = cancelingLobby;
    setIsCanceling(true);

    try {
      // 1. Tell backend to lift suspension and delete frozen save data
      await suspendDraft(target.draft_id, null, null, null, false);

      // 2. Clear local browser IndexedDB saves for this draft
      await clearFrozenSave(target.draft_id);

      // 3. Best-effort cleanup of in-memory draft if host
      try {
        await deleteDraft(target.draft_id);
      } catch {
        // Ignore 404 or non-host error
      }

      // 4. Optimistically update local state so UI updates instantly
      setLobbies((prev) => {
        const updated = prev.map((l) => {
          if (l.draft_id !== target.draft_id) return l;
          return {
            ...l,
            is_suspended: false,
            teams: l.teams.map((t) => ({
              ...t,
              is_suspended: false,
              suspended_at: null,
            })),
          };
        });
        return suspendedFilter ? updated.filter((l) => l.is_suspended) : updated;
      });

      showToast(`Active run "${target.draft_name || 'Solo Run'}" has been cancelled.`);
      setCancelingLobby(null);
    } catch (err: any) {
      console.error('[HistoryPage] Failed to cancel active run:', err);
      showToast(err?.message || 'Failed to cancel active run. Please try again.');
    } finally {
      setIsCanceling(false);
    }
  }, [cancelingLobby, suspendedFilter, showToast]);

  // Escape key closes cancel modal
  useEffect(() => {
    if (!cancelingLobby) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isCanceling) {
        setCancelingLobby(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cancelingLobby, isCanceling]);

  // Load lobbies from API
  const loadLobbies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let queryUserId: string | undefined = undefined;
      if (scope === 'my') {
        queryUserId = currentUser?.user_id || 'me';
      } else if (scope === 'user') {
        queryUserId = userParam || userNameParam || undefined;
      }

      const res = await fetchLobbyHistory({
        page,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
        format: formatFilter,
        user_id: queryUserId,
        is_suspended: suspendedFilter ? true : undefined,
      });
      setLobbies(res.lobbies);
      setTotal(res.total);

      // Auto-expand the first lobby if only 1 page and just loaded
      if (res.lobbies.length > 0 && expandedDraftIds.size === 0) {
        setExpandedDraftIds(new Set([res.lobbies[0].draft_id]));
      }
    } catch (err: any) {
      console.error('[HistoryPage] Failed to fetch lobby history:', err);
      setError('Could not load lobby history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, formatFilter, scope, suspendedFilter, currentUser, userParam, userNameParam]);

  useEffect(() => {
    loadLobbies();
  }, [loadLobbies]);

  // Expand / collapse single lobby
  const toggleLobby = (draftId: string) => {
    setExpandedDraftIds((prev) => {
      const next = new Set(prev);
      if (next.has(draftId)) {
        next.delete(draftId);
      } else {
        next.add(draftId);
      }
      return next;
    });
  };

  // Expand all / Collapse all
  const handleToggleAll = () => {
    if (expandedDraftIds.size === lobbies.length) {
      setExpandedDraftIds(new Set());
    } else {
      setExpandedDraftIds(new Set(lobbies.map((l) => l.draft_id)));
    }
  };

  // CSV download for a single lobby
  const handleDownloadCSV = (lobby: LobbyHistoryItem) => {
    let csv = `Draft Name: "${lobby.draft_name.replace(/"/g, '""')}"\n`;
    csv += `Draft ID: ${lobby.draft_id}\n`;
    csv += `Date: ${new Date(lobby.created_at).toLocaleString()}\n`;
    csv += `Format: ${formatDraftTypeLabel(lobby.draft_type, lobby.ranked)}\n`;
    csv += `Total Players: ${lobby.teams_count}\n\n`;

    csv += `=== DRAFT ROSTERS ===\n`;
    csv += `Player,Pick Order,Pokemon,Form,Winning Bid / Action\n`;
    lobby.teams.forEach((team) => {
      const safeName = `"${team.user_name.replace(/"/g, '""')}"`;
      team.pokemon_drafted.forEach((p) => {
        const bidOrAction = p.winning_bid !== null && p.winning_bid !== undefined
          ? `$${p.winning_bid}`
          : (p.action || 'PICK');
        csv += `${safeName},${p.draft_order},"${p.name}","${p.form || ''}",${bidOrAction}\n`;
      });
    });

    csv += `\n=== PLAYER ATTEMPTS & RESULTS ===\n`;
    csv += `Player,Placement,Total Attempts,Furthest Attempt,Attempt Number,Badges,Result,Time,Furthest?\n`;
    lobby.teams.forEach((team) => {
      const safeName = `"${team.user_name.replace(/"/g, '""')}"`;
      const placement = team.race_placement ?? team.placement ?? '-';
      const furthestSummary = team.furthest_attempt ? `"${team.furthest_attempt.result}"` : 'None';
      if (team.attempts.length === 0) {
        csv += `${safeName},${placement},0,${furthestSummary},-,0,No Attempts,-,No\n`;
      } else {
        team.attempts.forEach((att) => {
          csv += `${safeName},${placement},${team.total_attempts},${furthestSummary},${att.attempt_number},${att.badge_count},"${att.result.replace(/"/g, '""')}",${att.time || '-'},${att.is_furthest ? 'YES' : 'No'}\n`;
        });
      }
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `lobby-history-${lobby.draft_name.replace(/[^a-zA-Z0-9_-]/g, '_')}-${lobby.draft_id.slice(0, 8)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(total / pageSize);

  const handleScopeChange = (newScope: 'all' | 'my') => {
    setScope(newScope);
    setPage(1);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('user_id');
    nextParams.delete('userId');
    nextParams.delete('user');
    nextParams.delete('username');
    if (newScope === 'my') {
      nextParams.set('tab', 'my');
      nextParams.delete('scope');
    } else {
      nextParams.delete('tab');
      nextParams.delete('scope');
    }
    setSearchParams(nextParams, { replace: true });
  };

  const handleScopeToUser = (targetId?: string, targetName?: string) => {
    setScope('user');
    setPage(1);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('tab');
    nextParams.delete('scope');
    if (targetId) {
      nextParams.set('user_id', targetId);
    } else {
      nextParams.delete('user_id');
      nextParams.delete('userId');
    }
    if (targetName) {
      nextParams.set('user', targetName);
    } else {
      nextParams.delete('user');
      nextParams.delete('username');
    }
    setSearchParams(nextParams);
  };

  const isMySuspendedRun = useCallback(
    (lobby: LobbyHistoryItem) => {
      if (!lobby.is_suspended) return false;
      if (scope === 'my') return true;
      if (lobby.draft_type === 'solo') return true;
      if (!currentUser?.user_id && !currentUser?.username) return true;
      return lobby.teams.some(
        (t) =>
          t.is_suspended &&
          ((currentUser.user_id && (t.user_id === currentUser.user_id || t.guest_id === currentUser.user_id)) ||
            (currentUser.username && t.user_name?.toLowerCase() === currentUser.username.toLowerCase()))
      );
    },
    [scope, currentUser]
  );

  const mySavedRuns = useMemo(() => {
    return lobbies.filter((l) => isMySuspendedRun(l));
  }, [lobbies, isMySuspendedRun]);

  const scopedDisplayName = userNameParam || userParam || 'Player';

  return (
    <div className="history-page">
      <Header />
      <main className="history-main">
        {/* Scope Switcher: All Lobbies vs My Matches vs User Scoped */}
        <div className="history-scope-container">
          <div className="history-scope-tabs">
            <button
              type="button"
              className={`scope-tab ${scope === 'all' ? 'active' : ''}`}
              onClick={() => handleScopeChange('all')}
            >
              <span className="scope-icon">🌐</span>
              All Matches
            </button>
            <button
              type="button"
              className={`scope-tab ${scope === 'my' ? 'active' : ''}`}
              onClick={() => handleScopeChange('my')}
            >
              <span className="scope-icon">👤</span>
              My Matches & Runs
              {mySavedRuns.length > 0 && (
                <span className="scope-count-badge" title={`${mySavedRuns.length} frozen runs ready to resume`}>
                  {mySavedRuns.length} Saved
                </span>
              )}
            </button>
            {scope === 'user' && (
              <div className="scope-tab active user-scoped-tab">
                <span className="scope-icon">🎯</span>
                <span>{scopedDisplayName}'s Matches</span>
                <button
                  type="button"
                  className="scope-clear-btn"
                  onClick={() => handleScopeChange('all')}
                  title="Clear player scope and return to all matches"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Hero Section */}
        <section className="history-hero">
          <div className="history-hero-content">
            <div className="history-badge-pill">
              <span className="history-badge-dot"></span>
              {scope === 'user'
                ? `Player Archive: ${scopedDisplayName}`
                : scope === 'my'
                ? 'Personal Match Archive'
                : 'Draft & Match Archive'}
            </div>
            <h1 className="history-title">
              {scope === 'user'
                ? `${scopedDisplayName}'s Match History`
                : scope === 'my'
                ? 'My Match History'
                : 'Lobby History'}
            </h1>
            <p className="history-subtitle">
              {scope === 'user'
                ? `Review all past drafts, tournament races, and run results for ${scopedDisplayName}.`
                : scope === 'my'
                ? 'Review your past drafts, tournament races, solo runs, and frozen saves ready to resume.'
                : "Browse every lobby's full draft roster, player race attempts, and furthest runs reached."}
            </p>
          </div>

          <div className="history-stats-bar">
            <div className="history-stat-box">
              <span className="stat-label">
                {scope === 'user' ? `${scopedDisplayName} Matches` : scope === 'my' ? 'My Matches' : 'Total Lobbies'}
              </span>
              <span className="stat-value">{total.toLocaleString()}</span>
            </div>
            <div className="history-stat-box">
              <span className="stat-label">Active Filter</span>
              <span className="stat-value highlight">
                {suspendedFilter
                  ? 'Frozen Runs'
                  : formatFilter === 'all'
                  ? 'All Formats'
                  : formatFilter === 'ranked'
                  ? 'Ranked'
                  : formatFilter === 'casual'
                  ? 'Casual'
                  : formatFilter === '1v1'
                  ? '1v1'
                  : 'Solo'}
              </span>
            </div>
          </div>
        </section>

        {/* Guest user notice when in My Matches */}
        {scope === 'my' && !loading && currentUser?.is_guest && (
          <div className="history-guest-notice">
            <span className="notice-icon">💡</span>
            <div className="notice-text">
              <strong>Playing as a Guest ({currentUser.username}):</strong> Your runs on this browser are tracked here. To keep your match history and saved runs across all devices, consider logging in with Discord.
            </div>
          </div>
        )}

        {/* Scoped User Notice Banner */}
        {scope === 'user' && !loading && (
          <div className="history-scoped-user-notice">
            <div className="notice-content">
              <span className="notice-icon">🎯</span>
              <div className="notice-text">
                Showing match history scoped to <strong>{scopedDisplayName}</strong>.
              </div>
            </div>
            <button
              type="button"
              className="notice-clear-btn"
              onClick={() => handleScopeChange('all')}
              title="Return to all lobbies"
            >
              Show All Matches
            </button>
          </div>
        )}

        {/* Saved & Frozen Runs Quick Resume Banner */}
        {mySavedRuns.length > 0 && (
          <section className="history-saved-runs-panel">
            <div className="saved-runs-header">
              <div className="saved-runs-title-group">
                <span className="saved-runs-icon">💾</span>
                <div>
                  <h3 className="saved-runs-title">Frozen & Saved Runs</h3>
                  <p className="saved-runs-sub">
                    You have {mySavedRuns.length} run{mySavedRuns.length === 1 ? '' : 's'} saved and ready to resume right where you left off.
                  </p>
                </div>
              </div>
            </div>
            <div className="saved-runs-grid">
              {mySavedRuns.map((savedLobby) => (
                <div key={savedLobby.draft_id} className="saved-run-card">
                  <div className="saved-run-card-info">
                    <span className={`lobby-type-tag ${formatDraftTypeClass(savedLobby.draft_type, savedLobby.ranked)}`}>
                      {formatDraftTypeLabel(savedLobby.draft_type, savedLobby.ranked)}
                    </span>
                    <h4 className="saved-run-name">{savedLobby.draft_name || 'Solo Run'}</h4>
                    <span className="saved-run-date">
                      Saved {getTimeAgo(savedLobby.teams.find((t) => t.is_suspended)?.suspended_at || savedLobby.created_at)}
                    </span>
                  </div>
                  <div className="saved-run-card-actions">
                    <Link
                      to={`/Emulator/${savedLobby.draft_id}?resume=1`}
                      className="saved-run-resume-btn"
                    >
                      ▶ Resume Run
                    </Link>
                    <button
                      type="button"
                      className="saved-run-cancel-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePromptCancelRun(savedLobby);
                      }}
                      title="Cancel this active run"
                    >
                      ✕ Cancel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Filters and Controls */}
        <section className="history-controls">
          <div className="history-search-wrapper">
            <span className="search-icon">🔍</span>
            <input
              id="history-search-input"
              type="text"
              className="history-search-input"
              placeholder={scope === 'my' ? 'Search your matches...' : 'Search by lobby name, host, or player...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className="search-clear-btn"
                onClick={() => setSearch('')}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          <div className="history-filter-chips">
            <button
              type="button"
              className={`filter-chip ${formatFilter === 'all' && !suspendedFilter ? 'active' : ''}`}
              onClick={() => {
                setFormatFilter('all');
                setSuspendedFilter(false);
                setPage(1);
              }}
            >
              All Formats
            </button>
            <button
              type="button"
              className={`filter-chip ${formatFilter === 'ranked' && !suspendedFilter ? 'active' : ''}`}
              onClick={() => {
                setFormatFilter('ranked');
                setSuspendedFilter(false);
                setPage(1);
              }}
            >
              🏆 Ranked
            </button>
            <button
              type="button"
              className={`filter-chip ${formatFilter === 'casual' && !suspendedFilter ? 'active' : ''}`}
              onClick={() => {
                setFormatFilter('casual');
                setSuspendedFilter(false);
                setPage(1);
              }}
            >
              🎮 Casual
            </button>
            <button
              type="button"
              className={`filter-chip ${formatFilter === '1v1' && !suspendedFilter ? 'active' : ''}`}
              onClick={() => {
                setFormatFilter('1v1');
                setSuspendedFilter(false);
                setPage(1);
              }}
            >
              ⚔️ 1v1 Draft
            </button>
            <button
              type="button"
              className={`filter-chip ${formatFilter === 'solo' && !suspendedFilter ? 'active' : ''}`}
              onClick={() => {
                setFormatFilter('solo');
                setSuspendedFilter(false);
                setPage(1);
              }}
            >
              👤 Solo
            </button>
            <button
              type="button"
              className={`filter-chip ${suspendedFilter ? 'active suspended-filter' : ''}`}
              onClick={() => {
                setSuspendedFilter((prev) => !prev);
                setPage(1);
              }}
            >
              ⏸️ Saved / Frozen Runs
            </button>
          </div>

          <div className="history-actions-bar">
            {lobbies.length > 0 && (
              <button
                type="button"
                className="history-toggle-all-btn"
                onClick={handleToggleAll}
              >
                {expandedDraftIds.size === lobbies.length ? '▲ Collapse All' : '▼ Expand All'}
              </button>
            )}
            <button
              type="button"
              className="history-refresh-btn"
              onClick={loadLobbies}
              title="Refresh history"
            >
              🔄 Refresh
            </button>
          </div>
        </section>

        {/* Error message */}
        {error && (
          <div className="history-message-box error">
            <span className="msg-icon">⚠️</span> {error}
            <button type="button" onClick={loadLobbies} className="retry-btn">
              Retry
            </button>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="history-loading-container">
            <div className="history-spinner"></div>
            <p>Loading lobby history...</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && lobbies.length === 0 && (
          <div className="history-message-box empty">
            <span className="empty-icon">📂</span>
            <h3>No Lobbies Found</h3>
            <p>
              {debouncedSearch
                ? `No lobbies matched "${debouncedSearch}". Try another search term or filter.`
                : 'No lobbies have been recorded yet in this format.'}
            </p>
            {debouncedSearch && (
              <button
                type="button"
                className="filter-chip active"
                onClick={() => setSearch('')}
              >
                Clear Search
              </button>
            )}
          </div>
        )}

        {/* Lobbies List */}
        {!loading && !error && lobbies.length > 0 && (
          <div className="history-list">
            {lobbies.map((lobby) => {
              const isExpanded = expandedDraftIds.has(lobby.draft_id);
              const is1v1 = lobby.draft_type === '1v1';
              const formattedDate = new Date(lobby.created_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              });

              return (
                <article
                  key={lobby.draft_id}
                  className={`lobby-card ${isExpanded ? 'expanded' : ''}`}
                >
                  {/* Lobby Card Header / Summary */}
                  <div
                    className="lobby-header"
                    onClick={() => toggleLobby(lobby.draft_id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleLobby(lobby.draft_id);
                      }
                    }}
                  >
                    <div className="lobby-header-left">
                      <span className={`lobby-type-tag ${formatDraftTypeClass(lobby.draft_type, lobby.ranked)}`}>
                        {formatDraftTypeLabel(lobby.draft_type, lobby.ranked)}
                      </span>
                      <h2 className="lobby-name">{lobby.draft_name || 'Unnamed Lobby'}</h2>
                      <div className="lobby-meta">
                        <span className="lobby-date" title={new Date(lobby.created_at).toLocaleString()}>
                          📅 {formattedDate} ({getTimeAgo(lobby.created_at)})
                        </span>
                        {lobby.host_name && (
                          <span className="lobby-host">
                            👤 Hosted by <strong>{lobby.host_name}</strong>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="lobby-header-right">
                      {isMySuspendedRun(lobby) && (
                        <div className="lobby-header-run-actions" onClick={(e) => e.stopPropagation()}>
                          <Link
                            to={`/Emulator/${lobby.draft_id}?resume=1`}
                            className="lobby-header-resume-btn"
                            title="Resume playing this saved run"
                          >
                            ▶ Resume Run
                          </Link>
                          <button
                            type="button"
                            className="lobby-header-cancel-btn"
                            title="Cancel active run"
                            onClick={(e) => handlePromptCancelRun(lobby, e)}
                          >
                            ✕ Cancel
                          </button>
                        </div>
                      )}
                      {lobby.is_suspended && !isMySuspendedRun(lobby) && (
                        <div className="lobby-suspended-pill" title="This run is frozen and ready to resume">
                          <span className="pause-icon">⏸️</span>
                          <span className="suspended-label">Saved Run</span>
                        </div>
                      )}
                      {lobby.winner_name && (
                        <div className="lobby-winner-pill" title={`Winner: ${lobby.winner_name}`}>
                          <span className="trophy-icon">🏆</span>
                          <span className="winner-label">Winner:</span>
                          <strong className="winner-name">{lobby.winner_name}</strong>
                        </div>
                      )}
                      <div className="lobby-players-count">
                        👥 {lobby.teams_count} {lobby.teams_count === 1 ? 'Player' : 'Players'}
                      </div>
                      <button
                        type="button"
                        className={`lobby-expand-chevron ${isExpanded ? 'open' : ''}`}
                        aria-label={isExpanded ? 'Collapse lobby' : 'Expand lobby'}
                      >
                        ▼
                      </button>
                    </div>
                  </div>

                  {/* Expanded Lobby Content */}
                  {isExpanded && (
                    <div className="lobby-content">
                      {/* Lobby Actions Bar */}
                      <div className="lobby-actions-toolbar">
                        <div className="lobby-links">
                          {isMySuspendedRun(lobby) && (
                            <>
                              <Link
                                to={`/Emulator/${lobby.draft_id}?resume=1`}
                                className="lobby-action-link resume-run-btn"
                                title="Resume playing this saved run"
                              >
                                ▶ Resume Run
                              </Link>
                              <button
                                type="button"
                                className="lobby-action-link cancel-run-btn"
                                title="Cancel this active run"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePromptCancelRun(lobby, e);
                                }}
                              >
                                ✕ Cancel Run
                              </button>
                            </>
                          )}
                          <Link
                            to={`/Spectate/${lobby.draft_id}`}
                            className="lobby-action-link spectate-link"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            👁️ View Race / Spectate
                          </Link>
                          <Link
                            to={is1v1 ? `/Draft1v1?${lobby.draft_id}` : `/Auction?${lobby.draft_id}`}
                            className="lobby-action-link room-link"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            🏛️ View Draft Room
                          </Link>
                        </div>

                        <button
                          type="button"
                          className="lobby-action-link csv-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadCSV(lobby);
                          }}
                          title="Download Draft & Attempts CSV"
                        >
                          📄 Export CSV
                        </button>
                      </div>

                      {/* Players / Teams List */}
                      <div className="lobby-teams-grid">
                        {lobby.teams.length === 0 && (
                          <div className="no-teams-message">No team data found for this lobby.</div>
                        )}
                        {lobby.teams.map((team, idx) => {
                          const placementBadge = getPlacementBadge(team.placement, team.race_placement);
                          const userIdentifier = team.user_id || team.guest_id || '';
                          const avatarUrl = team.user_id
                            ? getCachedAvatarUrl(team.user_id, team.avatar)
                            : undefined;

                          const isTargetUser =
                            (scope === 'my' && Boolean(
                              currentUser?.user_id && (team.user_id === currentUser.user_id || team.guest_id === currentUser.user_id || team.user_name === currentUser.username)
                            )) ||
                            (scope === 'user' && Boolean(
                              (userParam && (team.user_id === userParam || team.guest_id === userParam)) ||
                              (userNameParam && (
                                team.user_name.toLowerCase() === userNameParam.toLowerCase() ||
                                (team.global_name && team.global_name.toLowerCase() === userNameParam.toLowerCase())
                              ))
                            ));

                          return (
                            <div key={team.team_id || idx} className={`team-card ${isTargetUser ? 'user-scoped-team' : ''}`}>
                              {/* Player Header */}
                              <div className="team-header">
                                <div className="team-user-info">
                                  {avatarUrl ? (
                                    <img
                                      src={avatarUrl}
                                      alt={team.user_name}
                                      className="team-user-avatar"
                                      onError={(e) => {
                                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                                      }}
                                    />
                                  ) : (
                                    <div className="team-user-avatar-placeholder">
                                      {team.user_name.charAt(0).toUpperCase()}
                                    </div>
                                  )}
                                  <div className="team-user-names">
                                    <div className="team-name-row">
                                      <Link
                                        to={`/Stats/PlayerProfiles/${encodeURIComponent(team.user_name)}${userIdentifier ? `?userId=${encodeURIComponent(userIdentifier)}` : ''}`}
                                        className="team-username"
                                        title="View player profile"
                                      >
                                        {team.global_name && team.global_name !== team.user_name ? (
                                          <>
                                            <span className="global-name">{team.global_name}</span>
                                            <span className="user-discriminator">@{team.user_name}</span>
                                          </>
                                        ) : (
                                          team.user_name
                                        )}
                                      </Link>
                                      {isTargetUser ? (
                                        <span className="target-user-badge">
                                          {scope === 'my' ? '⭐ You' : '⭐ Scoped Player'}
                                        </span>
                                      ) : (
                                        <button
                                          type="button"
                                          className="team-filter-history-btn"
                                          title={`Filter match history to ${team.user_name}`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleScopeToUser(userIdentifier, team.user_name);
                                          }}
                                        >
                                          📜
                                        </button>
                                      )}
                                    </div>
                                    <span className="team-budget-spent">
                                      Remaining: ${team.money_remaining.toLocaleString()}
                                    </span>
                                  </div>
                                </div>

                                <div className="team-header-badges">
                                  {placementBadge && (
                                    <span className={`placement-pill ${placementBadge.className}`}>
                                      {placementBadge.label}
                                    </span>
                                  )}
                                  <span className="attempts-count-pill" title={`${team.total_attempts} total run attempts`}>
                                    🔄 {team.total_attempts} {team.total_attempts === 1 ? 'Attempt' : 'Attempts'}
                                  </span>
                                </div>
                              </div>

                              {/* Frozen / Suspended Run Banner */}
                              {team.is_suspended && (
                                <div className="team-suspended-banner">
                                  <div className="suspended-badge">
                                    <span className="badge-icon">⏸️</span>
                                    <span className="suspended-badge-text">Run Saved & Frozen</span>
                                    {team.suspended_at && (
                                      <span className="suspended-date">({getTimeAgo(team.suspended_at)})</span>
                                    )}
                                  </div>
                                  <Link
                                    to={`/Emulator/${lobby.draft_id}?resume=1`}
                                    className="team-resume-btn"
                                  >
                                    ▶ Resume Playing
                                  </Link>
                                </div>
                              )}

                              {/* Furthest Attempt Highlight Banner */}
                              {team.furthest_attempt && (
                                <div className={`furthest-run-banner ${team.furthest_attempt.is_win ? 'is-win' : ''}`}>
                                  <div className="furthest-label">
                                    <span className="furthest-star">⭐</span>
                                    <span>Furthest Run (Attempt #{team.furthest_attempt.attempt_number})</span>
                                  </div>
                                  <div className="furthest-result">
                                    <span className="badge-count-pill">
                                      🏅 {team.furthest_attempt.badge_count} / 8 Badges
                                    </span>
                                    <strong className={`result-tag ${team.furthest_attempt.is_win ? 'win' : 'loss'}`}>
                                      {team.furthest_attempt.result}
                                    </strong>
                                    {team.furthest_attempt.time && (
                                      <span className="result-time">⏱️ {team.furthest_attempt.time}</span>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Drafted Pokémon Section */}
                              <div className="team-section draft-section">
                                <div className="section-title">
                                  <span>Drafted Team</span>
                                  <span className="count-pill">{team.pokemon_drafted.length} Pokémon</span>
                                </div>

                                {team.pokemon_drafted.length === 0 ? (
                                  <div className="no-draft-message">No Pokémon drafted.</div>
                                ) : (
                                  <div className="drafted-pokemon-list">
                                    {team.pokemon_drafted.map((mon) => {
                                      const isAction = !!mon.action && mon.action !== 'PICK';
                                      return (
                                        <div
                                          key={mon.auction_id}
                                          className={`pokemon-draft-chip ${isAction ? 'is-action' : ''}`}
                                          title={`${mon.name}${mon.form ? ` (${mon.form})` : ''} - ${mon.winning_bid !== null && mon.winning_bid !== undefined ? `$${mon.winning_bid.toLocaleString()}` : mon.action || 'Pick'}`}
                                        >
                                          <img
                                            src={`/MiniIcons/${formatPokemonName(mon.name)}.png`}
                                            alt={mon.name}
                                            className="pokemon-mini-icon"
                                            onError={(e) => {
                                              const target = e.currentTarget;
                                              if (!target.src.includes('/baseforms/')) {
                                                target.src = `/baseforms/${mon.name}.png`;
                                              } else {
                                                target.style.display = 'none';
                                              }
                                            }}
                                          />
                                          <div className="pokemon-chip-info">
                                            <span className="pokemon-name">{mon.name}</span>
                                            {mon.winning_bid !== null && mon.winning_bid !== undefined ? (
                                              <span className="pokemon-bid">${mon.winning_bid.toLocaleString()}</span>
                                            ) : (
                                              <span className="pokemon-action">{mon.action || `#${mon.draft_order}`}</span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>

                              {/* All Attempts Breakdown Section */}
                              <div className="team-section attempts-section">
                                <div className="section-title">
                                  <span>All Run Attempts</span>
                                  <span className="count-pill">{team.attempts.length} Recorded</span>
                                </div>

                                {team.attempts.length === 0 ? (
                                  <div className="no-attempts-message">
                                    No live attempt data recorded for this run.
                                  </div>
                                ) : (
                                  <div className="attempts-list">
                                    {team.attempts.map((att) => (
                                      <div
                                        key={att.attempt_number}
                                        className={`attempt-row ${att.is_furthest ? 'furthest' : ''} ${att.is_win ? 'win-row' : ''}`}
                                      >
                                        <div className="attempt-left">
                                          <span className="attempt-num">#{att.attempt_number}</span>
                                          <span className="attempt-badges">
                                            {att.badge_count} {att.badge_count === 1 ? 'Badge' : 'Badges'}
                                          </span>
                                        </div>

                                        <div className="attempt-center">
                                          <span className={`attempt-result-tag ${att.is_win ? 'win' : att.most_recent_loss_name ? 'loss' : 'neutral'}`}>
                                            {att.result}
                                          </span>
                                          {att.time && <span className="attempt-time">({att.time})</span>}
                                        </div>

                                        <div className="attempt-right">
                                          {att.is_furthest && (
                                            <span className="furthest-pill">
                                              ⭐ Furthest
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Hall of Fame Section (if Champion Beaten) */}
                              {team.hall_of_fame_team && team.hall_of_fame_team.length > 0 && (
                                <div className="team-section hof-section">
                                  <div className="section-title gold">
                                    <span>🏆 Hall of Fame Winning Team</span>
                                  </div>
                                  <div className="hof-pokemon-list">
                                    {team.hall_of_fame_team.map((hofMon, hIdx) => (
                                      <div key={hIdx} className="hof-pokemon-chip" title={hofMon.name}>
                                        <img
                                          src={`/MiniIcons/${formatPokemonName(hofMon.name)}.png`}
                                          alt={hofMon.name}
                                          className="hof-mini-icon"
                                          onError={(e) => {
                                            const target = e.currentTarget;
                                            if (!target.src.includes('/baseforms/')) {
                                              target.src = `/baseforms/${hofMon.name}.png`;
                                            } else {
                                              target.style.display = 'none';
                                            }
                                          }}
                                        />
                                        <span className="hof-name">{hofMon.name}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && totalPages > 1 && (
          <nav className="history-pagination" aria-label="Lobby history pages">
            <button
              type="button"
              className="page-btn prev-btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ◀ Previous
            </button>
            <div className="page-indicator">
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
              <span className="page-total">({total.toLocaleString()} total lobbies)</span>
            </div>
            <button
              type="button"
              className="page-btn next-btn"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next ▶
            </button>
          </nav>
        )}

        {/* Cancel Active Run Confirmation Modal */}
        {cancelingLobby && (
          <div
            className="history-cancel-modal-overlay"
            onClick={() => !isCanceling && setCancelingLobby(null)}
          >
            <div
              className="history-cancel-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="cancel-run-title"
            >
              <div className="cancel-modal-header">
                <span className="cancel-modal-icon">⚠️</span>
                <h3 id="cancel-run-title" className="cancel-modal-title">
                  Cancel Active Run?
                </h3>
              </div>
              <div className="cancel-modal-body">
                <p className="cancel-modal-desc">
                  Are you sure you want to cancel the active run for{' '}
                  <strong className="cancel-lobby-name">
                    {cancelingLobby.draft_name || 'Solo Run'}
                  </strong>
                  ?
                </p>
                <div className="cancel-modal-details">
                  <span className={`lobby-type-tag ${formatDraftTypeClass(cancelingLobby.draft_type, cancelingLobby.ranked)}`}>
                    {formatDraftTypeLabel(cancelingLobby.draft_type, cancelingLobby.ranked)}
                  </span>
                  <span className="cancel-modal-date">
                    Started {getTimeAgo(cancelingLobby.created_at)}
                  </span>
                </div>
                <p className="cancel-modal-warning">
                  This will discard your frozen save file and state progress. This action cannot be undone.
                </p>
              </div>
              <div className="cancel-modal-actions">
                <button
                  type="button"
                  className="cancel-modal-btn secondary"
                  onClick={() => setCancelingLobby(null)}
                  disabled={isCanceling}
                >
                  Keep Run
                </button>
                <button
                  type="button"
                  className="cancel-modal-btn danger"
                  onClick={handleConfirmCancelRun}
                  disabled={isCanceling}
                >
                  {isCanceling ? 'Cancelling...' : '✕ Yes, Cancel Run'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast Notification */}
        {toast && (
          <div className="history-toast" role="status">
            <span className="toast-icon">✓</span>
            <span>{toast}</span>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default HistoryPage;
